import { moveInOrOut } from '../action';
import { updateGroupSize } from '../action/group';
import { EventTypes, LayoutSizeType, TYPES } from '../render/constants';
import { MATRIX_UTILS, MatrixSerializer } from '../render/effects/matrix';
import { deserializePoints, serializePoints } from '../render/line';
import { getPaddingBox } from '../render/text';
import { createAlignCache, createAlignResult, getFrameAncestor, hasFrameAncestor } from './align';
import { isSemiTransparent } from './color';
import { applyGridSnappingForResize } from './grid';
import { addPointAtSegment, handleLineDoubleClick } from './interaction/line';
import { convertSegmentType } from './interaction/path';
import { scalePathByDimensions, scalePathNode } from './svg';
import { Rect } from './region';

export function captureMoveDistanceAction(hitTestContext) {
  // 记录 down -> up 过程中的最大移动距离
  const { provider } = hitTestContext;
  if (hitTestContext.type === EventTypes.down) {
    provider.maxMoveDistance = {
      x: 0,
      y: 0,
    };
  } else if (hitTestContext.type === EventTypes.move && provider.pointStart) {
    const { point } = hitTestContext;
    const deltaX = Math.abs(point.x - provider.pointStart.x);
    const deltaY = Math.abs(point.y - provider.pointStart.y);
    provider.maxMoveDistance.x = Math.max(provider.maxMoveDistance.x, deltaX);
    provider.maxMoveDistance.y = Math.max(provider.maxMoveDistance.y, deltaY);
    provider.isRealMove = provider.maxMoveDistance.y > 3 || provider.maxMoveDistance.x > 3;
  }
  return false;
}

export function captureStateAction(hitTestContext) {
  if (hitTestContext.type === EventTypes.down) {
    hitTestContext.provider.previousSelectedNodes = [...hitTestContext.selectedNodes];
    hitTestContext.provider.previousPointDownStart = {
      ...hitTestContext.point,
    };
  }
  return false;
}

export function moveModeAction(hitTestContext) {
  const provider = hitTestContext.provider;
  const { scale, action } = provider.stageContext.providers;
  if (provider.moveMode === 'move' || action.isSpaceKeyPressed) {
    provider.cursor.set('pointer');
    if (hitTestContext.type === EventTypes.down) {
      provider.pointStart = {
        ...hitTestContext.point,
      };
    } else if (hitTestContext.type === EventTypes.move) {
      const point = hitTestContext.point;
      if (provider.pointStart) {
        const x = (point.x - provider.pointStart.x) * scale.scale;
        const y = (point.y - provider.pointStart.y) * scale.scale;
        scale.translate(x, y);
      }
    } else if (hitTestContext.type === EventTypes.up) {
      provider.pointStart = null;
    }
    return true;
  } else {
    return false;
  }
}

export function hoverAction(hitTestContext) {
  const node = hitTestContext.shallowNode || hitTestContext.targetNode;
  const provider = hitTestContext.provider;
  const isMoving = provider.movingNodes && provider.movingNodes.length > 0;
  const isInAreaSelection =
    hitTestContext.targetRegion && hitTestContext.targetRegion.nodes.includes(node);
  const isResizeing = !!provider.movingHandler;
  if (
    node &&
    hitTestContext.type === EventTypes.move &&
    !isMoving &&
    !isResizeing &&
    !isInAreaSelection
  ) {
    hitTestContext.selection.addHover(node.id);
  }
  return false;
}

export function editTextAction(hitTestContext) {
  const selectedNodes = hitTestContext.selectedNodes;
  const node = hitTestContext.targetSelectedNode || hitTestContext.targetNode;
  const isTargetInSelection = selectedNodes.includes(node);
  const isEditing = hitTestContext.stageContext.providers.text.isEditing(node);

  // 如果正在编辑文字，检查是否点击在编辑的文字节点上
  if (isEditing && node && node.type === TYPES.text) {
    // 如果点击的是正在编辑的文字节点，不退出编辑状态
    if (isTargetInSelection) {
      return true;
    }
  }

  return false;
}

// 独立的文字选区 Action：在编辑文本时处理按下/拖拽/抬起，映射到 provider 的选择逻辑
export function textSelectionAction(hitTestContext) {
  const { stageContext, type, point } = hitTestContext;
  const textProvider = stageContext.providers.text;
  if (!textProvider) return false;
  const node = hitTestContext.targetNode || hitTestContext.depthNode || hitTestContext.shallowNode;
  const isEditing = node && node.type === TYPES.text && textProvider.isEditing(node);

  // 拖拽过程中，即使指针离开节点区域也需要持续更新
  if (type === EventTypes.move && textProvider.isSelecting) {
    textProvider.updateSelectionToPoint(point);
    return true;
  }

  if (!isEditing) return false;

  if (type === EventTypes.down) {
    // 确保隐藏输入框保持焦点（防止选区在 mouseup 时丢失）
    stageContext.providers.text.inputController.focus();
    textProvider.beginSelectionFromPoint(point);
    return true;
  }
  if (type === EventTypes.up) {
    if (textProvider.isSelecting) {
      textProvider.updateSelectionToPoint(point);
      textProvider.endSelection();
      // 选区应该保持，不要改为插入点；return true 即可
      return true;
    }
    // 单击定位
    return true;
  }
  return false;
}

export function hoverHandlerAction(hitTestContext) {
  const handler = hitTestContext.targetHandler;
  const provider = hitTestContext.provider;
  provider.cursor.set('');

  if (
    handler &&
    (hitTestContext.type === EventTypes.move || hitTestContext.type === EventTypes.down)
  ) {
    const isMultiSelect = hitTestContext.selectedNodes.length > 1;
    if (isMultiSelect) {
      if (hitTestContext.targetHandler) {
        const id = hitTestContext.targetHandler.id;
        hitTestContext.provider.hoverHandlerId = handler.id;
        provider.cursor.setById(id);
      }
    } else {
      const isInSelection = hitTestContext.stageContext.providers.selection.hasSelect(
        hitTestContext.targetNode?.id,
      );
      if (isInSelection) {
        hitTestContext.provider.hoverHandlerId = handler.id;
        if (hitTestContext.targetHandler) {
          const id = hitTestContext.targetHandler.id;
          provider.cursor.setById(id);
        }
      }
    }
  }

  return false;
}

export function selectByRegionAction(hitTestContext) {
  const node = hitTestContext.targetNode;
  const handler = hitTestContext.targetHandler;
  const provider = hitTestContext.provider;
  const stageContext = hitTestContext.stageContext;
  const noNode = !node || node.type === TYPES.frame;
  const isBrushMode = !!provider.brush;
  if (noNode && !isBrushMode && !handler && hitTestContext.type === EventTypes.down) {
    provider.areaStart = {
      ...hitTestContext.point,
    };
    return true;
  } else if (hitTestContext.type === EventTypes.up) {
    if (provider.areaStart) {
      const point = hitTestContext.point;
      const dx = Math.abs(point.x - provider.areaStart.x);
      const dy = Math.abs(point.y - provider.areaStart.y);
      if (dx < 5 && dy < 5) {
        // Treat as click if movement is very small
        const node = hitTestContext.targetNode;
        if (node && node.type === TYPES.frame) {
          hitTestContext.stageContext.api.selectNode([node.id]);
        } else {
          unselectAction(hitTestContext, EventTypes.up);
        }
      } else if (provider.areaStart && provider.areaEnd) {
        // Normalize selection rect to ensure positive width/height and top-left origin
        const x1 = Math.min(provider.areaStart.x, provider.areaEnd.x);
        const y1 = Math.min(provider.areaStart.y, provider.areaEnd.y);
        const w = Math.abs(provider.areaEnd.x - provider.areaStart.x);
        const h = Math.abs(provider.areaEnd.y - provider.areaStart.y);
        const rect = new Rect(x1, y1, w, h);
        if (rect.w < 20 || rect.h < 20) {
          provider.areaStart = null;
        }

        const nodes = hitTestContext.currentSelectableNodes;
        const matchedNodes = [];

        const frames = nodes.filter((node) => node.type === TYPES.frame);

        // Check all nodes for complete containment within selection area
        for (const node of frames) {
          const nodeRect = stageContext.api.getNodeRegion(node).rect;

          // Check if the entire node is completely within the selection area
          const isCompletelyContained =
            rect.contains(nodeRect.x, nodeRect.y) &&
            rect.contains(nodeRect.x + nodeRect.w, nodeRect.y) &&
            rect.contains(nodeRect.x + nodeRect.w, nodeRect.y + nodeRect.h) &&
            rect.contains(nodeRect.x, nodeRect.y + nodeRect.h);

          if (isCompletelyContained) {
            matchedNodes.push(node);
          }
        }

        if (!matchedNodes.length) {
          // Check all non-frame nodes for partial intersection with selection area
          for (const node of nodes) {
            if (node.type === TYPES.frame) continue;
            const nodeRect = stageContext.api.getNodeRegion(node).rect;

            // Check if the node intersects with the selection area
            const isIntersecting = rect.intersection(nodeRect);

            if (isIntersecting) {
              matchedNodes.push(node);
            }
          }
        }

        const ids = matchedNodes.map((node) => node.id);
        hitTestContext.stageContext.api.selectNode(ids);
      }
    }

    provider.areaStart = null;
  } else if (hitTestContext.type === EventTypes.move) {
    provider.areaEnd = {
      ...hitTestContext.point,
    };

    // Real-time selection while dragging selection area
    if (provider.areaStart) {
      const x1 = Math.min(provider.areaStart.x, provider.areaEnd.x);
      const y1 = Math.min(provider.areaStart.y, provider.areaEnd.y);
      const w = Math.abs(provider.areaEnd.x - provider.areaStart.x);
      const h = Math.abs(provider.areaEnd.y - provider.areaStart.y);
      const rect = new Rect(x1, y1, w, h);

      // Ignore very small drags to prevent noisy updates
      if (rect.w >= 20 && rect.h >= 20) {
        const nodes = hitTestContext.currentSelectableNodes;
        const matchedNodes = [];

        // 1) Prefer selecting fully-contained frames only
        const frames = nodes.filter((node) => node.type === TYPES.frame);
        for (const node of frames) {
          const nodeRect = stageContext.api.getNodeRegion(node).rect;
          const isCompletelyContained =
            rect.contains(nodeRect.x, nodeRect.y) &&
            rect.contains(nodeRect.x + nodeRect.w, nodeRect.y) &&
            rect.contains(nodeRect.x + nodeRect.w, nodeRect.y + nodeRect.h) &&
            rect.contains(nodeRect.x, nodeRect.y + nodeRect.h);
          if (isCompletelyContained) {
            matchedNodes.push(node);
          }
        }

        // 2) If no fully-contained frames, select intersecting non-frame nodes
        if (!matchedNodes.length) {
          for (const node of nodes) {
            if (node.type === TYPES.frame) continue;
            const nodeRect = stageContext.api.getNodeRegion(node).rect;
            const isIntersecting = rect.intersection(nodeRect);
            if (isIntersecting) {
              matchedNodes.push(node);
            }
          }
        }

        const ids = matchedNodes.map((node) => node.id);
        hitTestContext.stageContext.api.selectNode(ids);
      }
    }
  }
}

export function regionResizeAction(hitTestContext) {
  const provider = hitTestContext.provider;
  if (hitTestContext.type === EventTypes.down) {
    if (hitTestContext.targetHandler && hitTestContext.selectedNodes.length > 1) {
      const rect = hitTestContext.stageContext.api.getSelectedNodesRegion();
      provider.movingHandler = {
        id: hitTestContext.targetHandler.id,
        nodes: [...hitTestContext.selectedNodes],
        cache: {
          ...rect,
        },
        point: {
          ...hitTestContext.point,
        },
      };
      provider.hoverHandlerId = hitTestContext.targetHandler.id;
      return true;
    }
  } else if (hitTestContext.type === EventTypes.move) {
    if (provider.movingHandler && provider.movingHandler.nodes) {
      const isShiftKeyPressed = hitTestContext.stageContext.providers.action.isShiftKeyPressed;
      const cacheNode = provider.movingHandler.cache;
      const ratio = cacheNode.w / cacheNode.h;
      const moveX1 = hitTestContext.point.x - provider.movingHandler.point.x;
      const moveY1 = hitTestContext.point.y - provider.movingHandler.point.y;
      const rotation = cacheNode.rotation || 0;
      const angle = rotation * (Math.PI / 180);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const moveX = moveX1 * cos - moveY1 * sin;
      const moveY = moveX1 * sin + moveY1 * cos;

      // Apply grid snapping for region resize operations
      const adjustedMoveX = moveX;
      const adjustedMoveY = moveY;

      // Check if any of the selected nodes are inside frames (not frames themselves)
      const hasFrameNodes = provider.movingHandler.nodes.some(
        (node) => node.type !== TYPES.frame && hasFrameAncestor(node, hitTestContext.stageContext),
      );

      if (hasFrameNodes) {
        // Find the first frame ancestor (not frame nodes themselves)
        for (const node of provider.movingHandler.nodes) {
          if (node.type !== TYPES.frame) {
            const frameAncestor = getFrameAncestor(node, hitTestContext.stageContext);
            if (frameAncestor) {
              break;
            }
          }
        }
      }

      const node = provider.movingHandler.cache;
      let patch = {};
      switch (provider.movingHandler.id) {
        case 'topLeft':
          if (isShiftKeyPressed) {
            const newWidth = node.w - adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              x: node.x + adjustedMoveX,
              y: node.y + adjustedMoveY,
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              x: node.x + adjustedMoveX,
              y: node.y + adjustedMoveY,
              w: node.w - adjustedMoveX,
              h: node.h - adjustedMoveY,
            };
          }
          break;
        case 'topRight':
          if (isShiftKeyPressed) {
            const newWidth = node.w + adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              y: node.y + adjustedMoveY,
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              y: node.y + adjustedMoveY,
              w: node.w + adjustedMoveX,
              h: node.h - adjustedMoveY,
            };
          }
          break;
        case 'bottomLeft':
          if (isShiftKeyPressed) {
            const newWidth = node.w - adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              x: node.x + adjustedMoveX,
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              x: node.x + adjustedMoveX,
              w: node.w - adjustedMoveX,
              h: node.h + adjustedMoveY,
            };
          }
          break;
        case 'bottomRight':
          if (isShiftKeyPressed) {
            const newWidth = node.w + adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              w: node.w + adjustedMoveX,
              h: node.h + adjustedMoveY,
            };
          }
          break;
        case 'top':
          if (isShiftKeyPressed) {
            const newHeight = node.h - adjustedMoveY;
            const newWidth = newHeight * ratio;
            patch = {
              y: node.y + adjustedMoveY,
              h: newHeight,
              w: newWidth,
            };
          } else {
            patch = {
              y: node.y + adjustedMoveY,
              h: node.h - adjustedMoveY,
            };
          }
          break;
        case 'right':
          if (isShiftKeyPressed) {
            const newWidth = node.w + adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              w: node.w + adjustedMoveX,
            };
          }
          break;
        case 'bottom':
          if (isShiftKeyPressed) {
            const newHeight = node.h + adjustedMoveY;
            const newWidth = newHeight * ratio;
            patch = {
              h: newHeight,
              w: newWidth,
            };
          } else {
            patch = {
              h: node.h + adjustedMoveY,
            };
          }
          break;
        case 'left':
          if (isShiftKeyPressed) {
            const newWidth = node.w - adjustedMoveX;
            const newHeight = newWidth / ratio;
            patch = {
              x: node.x + adjustedMoveX,
              w: newWidth,
              h: newHeight,
            };
          } else {
            patch = {
              x: node.x + adjustedMoveX,
              w: node.w - adjustedMoveX,
            };
          }
          break;
        case 'rotate': {
          const region = hitTestContext.stageContext.api.getNodeRegion(node);
          const currentRotation = region.angle;
          const centerX = node.x + node.w / 2;
          const centerY = node.y + node.h / 2;
          const angle = Math.atan2(
            hitTestContext.point.y - centerY,
            hitTestContext.point.x - centerX,
          );
          const newRotation = (angle * 180) / Math.PI - 90;
          patch = {
            rotation: (newRotation + currentRotation) % 360,
          };
          break;
        }
      }

      const children = [...provider.movingHandler.nodes];
      const scaleX = (patch.w ?? node.w) / node.w;
      const scaleY = (patch.h ?? node.h) / node.h;

      // Group children by parentId
      const childrenGroups = {};
      children.forEach((child) => {
        const parentId = child.parentId || 'root';
        if (!childrenGroups[parentId]) {
          childrenGroups[parentId] = [];
        }
        childrenGroups[parentId].push(child);
      });

      // Process each group separately
      Object.entries(childrenGroups).forEach(([, groupChildren]) => {
        const child = groupChildren[0];
        const frame = hitTestContext.stageContext.api
          .getAncenstors(child.id)
          .find((child) => child.type === TYPES.frame || child.type === TYPES.group);
        const frameY = frame?.y ?? 0;
        const frameX = frame?.x ?? 0;
        updateChildrenRecursively(
          provider,
          groupChildren,
          {
            scaleX,
            scaleY,
          },
          {
            deltaX: (patch.x ?? node.x) - frameX - (node.x - frameX) * scaleX,
            deltaY: (patch.y ?? node.y) - frameY - (node.y - frameY) * scaleY,
          },
        );
      });
      updateGroupSize(
        provider.stageContext,
        children.map((node) => ({
          node: node,
        })),
      );
      provider.hoverHandlerId = provider.movingHandler.id;
      provider.onChange && provider.onChange();
      hitTestContext.stageContext.hooks.onNodeResizeCallback();
      return true;
    }
  } else if (hitTestContext.type === EventTypes.up) {
    provider.movingHandler = null;
  }
}

export function selectAction(hitTestContext) {
  const node = hitTestContext.shallowNode || hitTestContext.targetNode;
  if (node && hitTestContext.type === EventTypes.down) {
    hitTestContext.stageContext.api.selectNode(node.id);
    return true;
  } else if (node && hitTestContext.type === EventTypes.up && hitTestContext.point.fromStage) {
    const selectedNodes = hitTestContext.provider.previousSelectedNodes;
    const isSingleNode = selectedNodes?.length === 1;
    const inSelection =
      selectedNodes &&
      selectedNodes.filter((selectedNode) => selectedNode.id === node.id).length > 0;
    const pointStart = hitTestContext.provider.previousPointDownStart;
    const isMoved =
      pointStart &&
      (Math.abs(hitTestContext.point.x - pointStart.x) > 3 ||
        Math.abs(hitTestContext.point.y - pointStart.y) > 3);
    const isRealMove = hitTestContext.provider.isRealMove;
    const skipSelectFromUp = hitTestContext.provider.skipSelectFromUp;
    if (!isRealMove && !isMoved && isSingleNode && inSelection && node.type === TYPES.text) {
      hitTestContext.stageContext.providers.text.create(node);
    } else if (!isMoved && !skipSelectFromUp) {
      hitTestContext.stageContext.api.selectNode(node.id);
    }
  }
}

export function unselectAction(hitTestContext, type = EventTypes.down) {
  const node = hitTestContext.targetNode;
  const handler = hitTestContext.targetHandler;
  const region = hitTestContext.targetRegion;
  const isEmpty = !region && !node && !handler;
  if (isEmpty && hitTestContext.type === type) {
    hitTestContext.selection.clearSelect();
    hitTestContext.provider.exitEditMode?.();
  }
}

export function regionRotateAction(hitTestContext) {
  const provider = hitTestContext.provider;

  // Check if this is a multi-selection rotation action
  const isSelectionRotate =
    hitTestContext.targetHandler &&
    hitTestContext.targetHandler.type === 'rotate' &&
    hitTestContext.selectedNodes.length > 1 &&
    hitTestContext.targetHandler.node == null;

  if (hitTestContext.type === EventTypes.down && isSelectionRotate) {
    provider.pointStart = {
      ...hitTestContext.point,
    };

    // Get the region bounds for calculating rotation center
    const rect = hitTestContext.stageContext.api.getSelectedNodesRegion();
    const centerX = rect.x + rect.w / 2;
    const centerY = rect.y + rect.h / 2;

    // Calculate initial angle from center to mouse position
    const startAngle = Math.atan2(
      hitTestContext.point.y - centerY,
      hitTestContext.point.x - centerX,
    );

    // Prepare selected nodes for rotation
    provider.movingNodes = hitTestContext.selectedNodes
      .filter((node) => node.type !== TYPES.frame)
      .map((node) => {
        return {
          id: node.id,
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          node: node,
          transform: node.transform || null, // Store original transform
        };
      });

    // Store rotation state
    provider.rotationState = {
      center: {
        x: centerX,
        y: centerY,
      },
      startAngle: startAngle,
      baseRotation: 0,
      baseNodes: provider.movingNodes.map((n) => ({
        id: n.id,
        transform: n.transform,
      })),
    };

    // Set hover handler for visual feedback
    provider.hoverHandlerId = hitTestContext.targetHandler.id;

    return true;
  } else if (hitTestContext.type === EventTypes.move && provider.rotationState) {
    const { center, startAngle, baseRotation } = provider.rotationState;

    // Calculate current angle from center to mouse position
    const currentAngle = Math.atan2(
      hitTestContext.point.y - center.y,
      hitTestContext.point.x - center.x,
    );

    // Calculate rotation delta
    const deltaAngle = currentAngle - startAngle;
    let newRotationDeg = ((baseRotation + deltaAngle) * 180) / Math.PI;

    // Apply 15-degree steps when Shift key is pressed
    if (hitTestContext.stageContext.providers.action.isShiftKeyPressed) {
      newRotationDeg = Math.round(newRotationDeg / 15) * 15;
    }

    // Convert to radians for calculations
    const rotationRad = (newRotationDeg * Math.PI) / 180;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);

    // Rotate each node around the selection center
    for (const movingNode of provider.movingNodes) {
      const node = movingNode.node;

      // Calculate the node's center relative to the selection center
      const nodeCenterX = movingNode.x + movingNode.w / 2;
      const nodeCenterY = movingNode.y + movingNode.h / 2;
      const relativeX = nodeCenterX - center.x;
      const relativeY = nodeCenterY - center.y;

      // Apply rotation transformation to the relative position
      const rotatedX = relativeX * cos - relativeY * sin;
      const rotatedY = relativeX * sin + relativeY * cos;

      // Calculate new position (top-left corner)
      const newX = center.x + rotatedX - movingNode.w / 2;
      const newY = center.y + rotatedY - movingNode.h / 2;

      // Get the original transform matrix for this node
      let baseMatrix = MATRIX_UTILS.identity();
      const originalTransform = movingNode.transform;
      if (typeof originalTransform === 'string') {
        const m = MatrixSerializer.deserialize(originalTransform);
        if (m) baseMatrix = m;
      }

      // Apply the rotation to the node's transform matrix
      const newMatrix = MATRIX_UTILS.setRotation(baseMatrix, newRotationDeg);
      const serialized = MatrixSerializer.serialize(newMatrix);

      // Update node position and rotation
      const updatePatch = {
        x: newX,
        y: newY,
        transform: serialized,
        rotation: null,
      };

      provider.stageContext.api.updateNode(node.id, updatePatch);
    }

    // Update hover handler for visual feedback
    provider.hoverHandlerId = provider.rotationState ? hitTestContext.targetHandler?.id : null;

    // Trigger change callbacks
    provider.onChange && provider.onChange();
    hitTestContext.stageContext.hooks.onNodeResizeCallback();

    return true;
  } else if (hitTestContext.type === EventTypes.up && provider.rotationState) {
    // Clean up rotation state
    provider.rotationState = null;
    provider.movingNodes = null;
    provider.pointStart = null;
    provider.hoverHandlerId = null;

    return true;
  }

  return false;
}

export function insertAction(hitTestContext) {
  if (!hitTestContext.provider.insertNode) return;
  if (hitTestContext.type === EventTypes.down) {
    const newNode = hitTestContext.provider.insertNode;
    const provider = hitTestContext.provider;
    provider.insertNode = null;
    provider.pointStart = {
      ...hitTestContext.point,
    };
    const node = hitTestContext.stageContext.api.addNode({
      ...newNode,
      x: hitTestContext.point.x - newNode.w / 2,
      y: hitTestContext.point.y - newNode.h / 2,
    });
    provider.insertedNode = node;
    const movingNodes = [
      {
        id: node.id,
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
        node: node,
      },
    ];
    moveInOrOut(hitTestContext, movingNodes);
    hitTestContext.stageContext.api.selectNode([node.id]);

    provider.movingHandler = {
      id: 'bottomRight',
      node: node,
      cache: {
        ...node,
      },
      point: {
        ...hitTestContext.point,
        x: hitTestContext.point.x + node.w / 2,
        y: hitTestContext.point.y + node.h / 2,
      },
    };
    provider.hoverHandlerId = 'bottomRight';
  } else if (hitTestContext.type === EventTypes.move) {
    if (!hitTestContext.provider.insertedNode) {
      hitTestContext.provider.cursor.setById('insert');
    }
  } else if (hitTestContext.type === EventTypes.up) {
    if (hitTestContext.provider.insertedNode) {
      const node = hitTestContext.provider.insertedNode;
      hitTestContext.provider.insertedNode = null;
      if (node.type === TYPES.text) {
        hitTestContext.stageContext.providers.text.create(node);
      } else {
        const canvas = hitTestContext.stageContext.context.canvas;
        canvas.parentNode.focus();
      }
    }
  }
  return true;
}

export function doubleClickAction(hitTestContext) {
  const node = hitTestContext.depthNode || hitTestContext.shallowNode;
  const provider = hitTestContext.provider;
  const stageContext = hitTestContext.stageContext;

  const apiNow =
    stageContext && stageContext.api && typeof stageContext.api.now === 'function'
      ? () => stageContext.api.now()
      : () => (performance && performance.now ? performance.now() : Date.now());
  const lastDown = provider.lastDown || apiNow();
  const cost = apiNow() - lastDown;

  if (hitTestContext.type === EventTypes.down) {
    provider.lastPoint = {
      ...hitTestContext.point,
    };
    provider.lastDown = apiNow();
  }

  if (hitTestContext.type === EventTypes.down && cost > 120 && cost < 400) {
    const handler = hitTestContext.targetHandler;
    // 优先处理 Path 段双击：中点 → 转换/插点
    if (handler && handler.type === 'pathMidPoint') {
      const pathNode = handler.node;
      if (pathNode && pathNode.type === TYPES.path) {
        const { action } = stageContext.providers;
        const toType = action.isShiftKeyPressed ? 'C' : 'Q';
        const newPath = convertSegmentType(pathNode.path, handler.segmentIndex, toType);
        stageContext.api.updateNode(pathNode.id, {
          path: newPath,
        });
        provider.lastDown = apiNow();
        return true;
      }
    }

    if (!node) return false;
    if (stageContext.api.shouldSkip(node)) return;
    if (stageContext.api.shouldSkip(node)) return;

    // 特殊处理 Line 节点的双击
    if (node.type === TYPES.line) {
      return handleLineDoubleClick(hitTestContext);
    }

    const selectedNodes = stageContext.api.getSelectedNodes();
    if (selectedNodes.length === 1 && selectedNodes.includes(node)) {
      if (node.type === TYPES.text) {
        stageContext.providers.text.create(node);
      } else if (node.type === TYPES.image) {
        provider.enterClipMode?.(node);
        stageContext.api.selectNode(node.id);
        provider.lastDown = apiNow();
        return true;
      } else if (node.type === TYPES.path) {
        provider.enterPathMode?.(node);
        stageContext.api.selectNode(node.id);
        provider.lastDown = Date.now();
        return true;
      } else {
        const isTransparent = typeof node.opacity === 'number' && node.opacity < 1;
        const isFillSemiTransparent = isSemiTransparent(node.fill);
        if (isTransparent || isFillSemiTransparent) {
          const newHitTestContext = provider.createHitTestContext(
            hitTestContext.point,
            {},
            hitTestContext.type,
            hitTestContext.stageContext,
            {
              skipIds: [node.id],
            },
          );
          provider.skipSelectFromUp = true;
          const targetNode = newHitTestContext.shallowNode || newHitTestContext.targetNode;
          if (targetNode) {
            stageContext.api.selectNode(targetNode.id);
            return true;
          }
        }
      }
    } else {
      stageContext.api.selectNode(node.id);
    }
    provider.lastDown = Date.now();
    return true;
  }
}

const pointerHandlerTypes = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];
export function resizeAction(hitTestContext) {
  const provider = hitTestContext.provider;
  if (hitTestContext.type === EventTypes.down) {
    if (hitTestContext.targetHandler) {
      const node = hitTestContext.targetHandler.node;

      if (hitTestContext.stageContext.api.shouldSkip(node)) return false;

      // 特殊处理Line节点的控制点
      if (node.type === TYPES.line && hitTestContext.targetHandler.type === 'linePoint') {
        return handleLinePointResize(hitTestContext);
      }
      // 检查是否点击在线段中点上
      if (hitTestContext.targetHandler && hitTestContext.targetHandler.type === 'lineMidPoint') {
        addPointAtSegment(hitTestContext);
        return true;
      }

      // 支持多选区域旋转：当点击的是多选区域的 rotate handler
      const isSelectionRotate =
        hitTestContext.targetHandler.type === 'rotate' &&
        hitTestContext.selectedNodes.length > 1 &&
        hitTestContext.targetHandler.node == null;

      provider.movingHandler = {
        type: hitTestContext.targetHandler.type,
        id: hitTestContext.targetHandler.id,
        node: isSelectionRotate ? null : hitTestContext.targetHandler.node,
        cache: {
          ...(hitTestContext.targetHandler.node || {}),
        },
        // 旋转时需要记录初始角度与初始指向
        rotate:
          hitTestContext.targetHandler.type === 'rotate'
            ? (() => {
                if (isSelectionRotate) {
                  const rect = hitTestContext.stageContext.api.getSelectedNodesRegion();
                  const cx = rect.x + rect.w / 2;
                  const cy = rect.y + rect.h / 2;
                  return {
                    selection: true,
                    center: {
                      x: cx,
                      y: cy,
                    },
                    startAngle: Math.atan2(
                      hitTestContext.point.y - cy,
                      hitTestContext.point.x - cx,
                    ),
                    baseRotation: 0,
                    baseNodes: hitTestContext.selectedNodes.map((n) => ({
                      id: n.id,
                      transform: n.transform || null,
                    })),
                  };
                } else {
                  const n = hitTestContext.targetHandler.node;
                  // Use region center to account for parent transforms (e.g., group)
                  const region = hitTestContext.stageContext.api.getNodeRegion(n);
                  const cx = region.center.x;
                  const cy = region.center.y;
                  return {
                    selection: false,
                    center: {
                      x: cx,
                      y: cy,
                    },
                    startAngle: Math.atan2(
                      hitTestContext.point.y - cy,
                      hitTestContext.point.x - cx,
                    ),
                    baseRotation: (region.angle * Math.PI) / 180,
                  };
                }
              })()
            : null,
        point: {
          ...hitTestContext.point,
        },
      };
      provider.hoverHandlerId = hitTestContext.targetHandler.id;
      return true;
    }
  } else if (hitTestContext.type === EventTypes.move) {
    if (provider.movingHandler) {
      const isShiftKeyPressed = hitTestContext.stageContext.providers.action.isShiftKeyPressed;
      const cacheNode = provider.movingHandler.cache || provider.movingHandler.node || {};

      if (
        cacheNode.type === TYPES.line &&
        provider.movingHandler &&
        provider.movingHandler.type === 'linePoint'
      ) {
        return handleLinePointResize(hitTestContext);
      }
      if (
        cacheNode.type === TYPES.line &&
        provider.movingHandler &&
        provider.movingHandler.type === 'lineMidPoint'
      ) {
        return;
      }

      // 旋转：基于按下时的相对角度差更新
      if (provider.movingHandler.type === 'rotate' && provider.movingHandler.rotate) {
        const { center, startAngle, baseRotation, selection, baseNodes } =
          provider.movingHandler.rotate;
        const curr = Math.atan2(
          hitTestContext.point.y - center.y,
          hitTestContext.point.x - center.x,
        );
        const delta = curr - startAngle;
        let newRotationDeg = ((baseRotation + delta) * 180) / Math.PI;
        // Shift 键：对齐到 15 度步进
        if (hitTestContext.stageContext.providers.action.isShiftKeyPressed) {
          newRotationDeg = Math.round(newRotationDeg / 15) * 15;
        }
        const normalized = ((((newRotationDeg + 180) % 360) + 360) % 360) - 180;
        if (selection && Array.isArray(baseNodes)) {
          // 旋转所有选中节点，基于它们各自的原始矩阵
          for (const { id, transform } of baseNodes) {
            let baseMatrix = MATRIX_UTILS.identity();
            if (typeof transform === 'string') {
              const m = MatrixSerializer.deserialize(transform);
              if (m) baseMatrix = m;
            }
            const newMatrix = MATRIX_UTILS.setRotation(baseMatrix, normalized);
            const serialized = MatrixSerializer.serialize(newMatrix);
            provider.stageContext.api.updateNode(id, {
              transform: serialized,
              rotation: null,
            });
          }
        } else {
          const node = provider.getMovingNodeInitialBox(provider.movingHandler.cache.id);
          let baseMatrix = MATRIX_UTILS.identity();
          if (typeof provider.movingHandler.cache.transform === 'string') {
            const m = MatrixSerializer.deserialize(provider.movingHandler.cache.transform);
            if (m) baseMatrix = m;
          }
          const newMatrix = MATRIX_UTILS.setRotation(baseMatrix, normalized);
          const serialized = MatrixSerializer.serialize(newMatrix);
          provider.stageContext.api.updateNode(node.id, {
            transform: serialized,
            rotation: null,
          });
        }
        provider.hoverHandlerId = provider.movingHandler.id;
        provider.onChange && provider.onChange();
        hitTestContext.stageContext.hooks.onNodeResizeCallback();
        return true;
      }

      // 计算原始鼠标移动量（非旋转）
      const rawMoveX = hitTestContext.point.x - provider.movingHandler.point.x;
      const rawMoveY = hitTestContext.point.y - provider.movingHandler.point.y;

      // 获取节点在全局坐标系下的区域信息（包含旋转）
      const region = hitTestContext.stageContext.api.getNodeRegion(provider.movingHandler.cache);
      const angle = (region.angle * Math.PI) / 180; // 转换为弧度
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      // 将鼠标移动量从全局坐标系转换到节点的局部坐标系
      let moveX1 = rawMoveX * cos + rawMoveY * sin;
      let moveY1 = -rawMoveX * sin + rawMoveY * cos;

      const stageContext = hitTestContext.stageContext;
      const point = (() => {
        const node = provider.getMovingNodeInitialBox(provider.movingHandler.cache.id);
        switch (provider.movingHandler.id) {
          case 'topLeft':
            return {
              x: node.x,
              y: node.y,
            };
          case 'topRight':
            return {
              x: node.x + node.w,
              y: node.y,
            };
          case 'bottomLeft':
            return {
              x: node.x,
              y: node.y + node.h,
            };
          case 'bottomRight':
            return {
              x: node.x + node.w,
              y: node.y + node.h,
            };
          case 'top':
            return {
              x: node.x + node.w / 2,
              y: node.y,
            };
          case 'bottom':
            return {
              x: node.x + node.w / 2,
              y: node.y + node.h,
            };
          case 'left':
            return {
              x: node.x,
              y: node.y + node.h / 2,
            };
          case 'right':
            return {
              x: node.x + node.w,
              y: node.y + node.h / 2,
            };
          default:
            return {
              x: node.x + node.w,
              y: node.y + node.h / 2,
            };
        }
      })();

      point.key = cacheNode.id;

      // Apply grid snapping candidate for resize (compute in world space, convert back to local)
      const adjustedMoveX1 = moveX1;
      const adjustedMoveY1 = moveY1;
      let gridWorldDx = null;
      let gridWorldDy = null;
      let gridLocalDx = null;
      let gridLocalDy = null;
      const currentNode = provider.movingHandler.node;
      if (
        currentNode &&
        currentNode.type !== TYPES.frame &&
        hasFrameAncestor(currentNode, stageContext)
      ) {
        const frame = getFrameAncestor(currentNode, stageContext);
        if (frame) {
          const gridRes = applyGridSnappingForResize(
            frame,
            provider.movingHandler,
            rawMoveX,
            rawMoveY,
            stageContext,
          );
          if (gridRes && (Number.isFinite(gridRes.deltaX) || Number.isFinite(gridRes.deltaY))) {
            gridWorldDx = gridRes.deltaX;
            gridWorldDy = gridRes.deltaY;
            // Convert to local
            gridLocalDx = gridWorldDx * cos + gridWorldDy * sin;
            gridLocalDy = -gridWorldDx * sin + gridWorldDy * cos;
          }
        }
      }

      const { action: actionProvider } = stageContext.providers;
      // Persist per-gesture align cache to stabilize snapping during resize
      if (!provider.alignCache) {
        // Freeze initial box at gesture start to stabilize snapping
        const initialRect = hitTestContext.stageContext.api.getNodeRegion(
          provider.movingHandler.cache,
        ).rect;
        provider.resizeInitialRect = {
          x: initialRect.x,
          y: initialRect.y,
          w: initialRect.w,
          h: initialRect.h,
        };
        provider.alignCache = createAlignCache(
          stageContext,
          provider.resizeInitialRect,
          [provider.movingHandler],
          adjustedMoveX1,
          adjustedMoveY1,
          point,
          'resize',
        );
      }
      // Use local-axis deltas for resize snapping; compute per-axis independently to allow dual-edge snapping
      let result = null;
      let resultX = null;
      let resultY = null;
      if (!actionProvider.isMetaKeyPressed) {
        resultX = createAlignResult(
          stageContext,
          provider.resizeInitialRect,
          [provider.movingHandler],
          adjustedMoveX1,
          0,
          point,
          'resize',
          provider.alignCache,
        );
        resultY = createAlignResult(
          stageContext,
          provider.resizeInitialRect,
          [provider.movingHandler],
          0,
          adjustedMoveY1,
          point,
          'resize',
          provider.alignCache,
        );
        // Build a combined result for visuals
        if (resultX || resultY) {
          result = {
            delta: {
              left: resultX ? resultX.delta.left : 0,
              top: resultY ? resultY.delta.top : 0,
            },
            lines: [...(resultX?.lines || []), ...(resultY?.lines || [])],
            highlightUntil: Math.max(resultX?.highlightUntil || 0, resultY?.highlightUntil || 0),
          };
        }
      }

      // Choose best per-axis correction between object guides and grid
      if (result) {
        // Object-based per-axis deltas are local
        const objLocalDx = resultX ? resultX.delta.left : moveX1;
        const objLocalDy = resultY ? resultY.delta.top : moveY1;
        const baseLocalDx = moveX1;
        const baseLocalDy = moveY1;
        const objShiftX = Math.abs(objLocalDx - baseLocalDx);
        const objShiftY = Math.abs(objLocalDy - baseLocalDy);
        const gridShiftX = gridLocalDx == null ? Infinity : Math.abs(gridLocalDx - baseLocalDx);
        const gridShiftY = gridLocalDy == null ? Infinity : Math.abs(gridLocalDy - baseLocalDy);
        const useGridX = Number.isFinite(gridShiftX) && gridShiftX > 0 && gridShiftX < objShiftX;
        const useGridY = Number.isFinite(gridShiftY) && gridShiftY > 0 && gridShiftY < objShiftY;
        moveX1 = useGridX ? gridLocalDx : objLocalDx;
        moveY1 = useGridY ? gridLocalDy : objLocalDy;
        // Update combined result visuals
        if (result) {
          result.delta.left = moveX1;
          result.delta.top = moveY1;
        }
        stageContext.interactive.alignmentResult = result || null;
      } else {
        // Fallback: if no object snap, but grid provides a correction, use it
        const baseDx = rawMoveX;
        const baseDy = rawMoveY;
        const gridShiftX = gridWorldDx == null ? 0 : Math.abs(gridWorldDx - baseDx);
        const gridShiftY = gridWorldDy == null ? 0 : Math.abs(gridWorldDy - baseDy);
        if (gridShiftX > 0 || gridShiftY > 0) {
          if (gridLocalDx != null) moveX1 = gridLocalDx;
          if (gridLocalDy != null) moveY1 = gridLocalDy;
        }
        stageContext.interactive.alignmentResult = null;
      }

      // Ensure re-render to update alignment lines during resize
      stageContext.api.markNeedRender();

      const node = provider.getMovingNodeInitialBox(provider.movingHandler.cache.id);
      let patch = {};
      const handleId = provider.movingHandler.id;

      if (handleId === 'rotate') {
        const region = hitTestContext.stageContext.api.getNodeRegion(node);
        const currentRotation = region.angle;
        const centerX = node.x + node.w / 2;
        const centerY = node.y + node.h / 2;
        const angle = Math.atan2(
          hitTestContext.point.y - centerY,
          hitTestContext.point.x - centerX,
        );
        const newRotation = (angle * 180) / Math.PI - 90;
        patch = {
          rotation: (newRotation + currentRotation) % 360,
        };
      } else {
        let newW = node.w;
        let newH = node.h;

        if (isShiftKeyPressed) {
          // 保持比例缩放
          const ratio = node.w / node.h;
          switch (handleId) {
            case 'topLeft': {
              newW = node.w - moveX1;
              newH = newW / ratio;
              break;
            }
            case 'topRight': {
              newW = node.w + moveX1;
              newH = newW / ratio;
              break;
            }
            case 'bottomLeft': {
              newW = node.w - moveX1;
              newH = newW / ratio;
              break;
            }
            case 'bottomRight': {
              newW = node.w + moveX1;
              newH = newW / ratio;
              break;
            }
            case 'top': {
              newH = node.h - moveY1;
              newW = newH * ratio;
              break;
            }
            case 'right': {
              newW = node.w + moveX1;
              newH = newW / ratio;
              break;
            }
            case 'bottom': {
              newH = node.h + moveY1;
              newW = newH * ratio;
              break;
            }
            case 'left': {
              newW = node.w - moveX1;
              newH = newW / ratio;
              break;
            }
          }
        } else {
          // 自由缩放
          switch (handleId) {
            case 'topLeft': {
              newW = node.w - moveX1;
              newH = node.h - moveY1;
              break;
            }
            case 'topRight': {
              newW = node.w + moveX1;
              newH = node.h - moveY1;
              break;
            }
            case 'bottomLeft': {
              newW = node.w - moveX1;
              newH = node.h + moveY1;
              break;
            }
            case 'bottomRight': {
              newW = node.w + moveX1;
              newH = node.h + moveY1;
              break;
            }
            case 'top': {
              newH = node.h - moveY1;
              break;
            }
            case 'right': {
              newW = node.w + moveX1;
              break;
            }
            case 'bottom': {
              newH = node.h + moveY1;
              break;
            }
            case 'left': {
              newW = node.w - moveX1;
              break;
            }
          }
        }

        // Alt 键：强制正方形（自身正方形策略）
        if (actionProvider.isAltKeyPressed) {
          const isCorner =
            handleId === 'topLeft' ||
            handleId === 'topRight' ||
            handleId === 'bottomLeft' ||
            handleId === 'bottomRight';
          if (isCorner) {
            // 根据主要拖拽方向选择参考边
            if (Math.abs(moveX1) >= Math.abs(moveY1)) {
              newH = newW;
            } else {
              newW = newH;
            }
          } else if (handleId === 'left' || handleId === 'right') {
            // 水平边：以宽为准
            newH = newW;
          } else if (handleId === 'top' || handleId === 'bottom') {
            // 垂直边：以高为准
            newW = newH;
          }
        }

        // 确保最小尺寸
        newW = Math.max(1, newW);
        newH = Math.max(1, newH);

        // 定义锚点映射，用于确定缩放时的固定点
        const oppositeHandles = {
          topLeft: 'bottomRight',
          topRight: 'bottomLeft',
          bottomLeft: 'topRight',
          bottomRight: 'topLeft',
          top: 'bottom',
          bottom: 'top',
          left: 'right',
          right: 'left',
        };
        const anchorId = oppositeHandles[handleId];

        // 在局部坐标系中获取锚点位置
        const getLocalHandleCoords = (id, w, h) => {
          const coords = {
            topLeft: {
              x: -w / 2,
              y: -h / 2,
            },
            topRight: {
              x: w / 2,
              y: -h / 2,
            },
            bottomLeft: {
              x: -w / 2,
              y: h / 2,
            },
            bottomRight: {
              x: w / 2,
              y: h / 2,
            },
            top: {
              x: 0,
              y: -h / 2,
            },
            bottom: {
              x: 0,
              y: h / 2,
            },
            left: {
              x: -w / 2,
              y: 0,
            },
            right: {
              x: w / 2,
              y: 0,
            },
          };
          return coords[id];
        };

        // 计算当前节点中心点
        const cx = node.x + node.w / 2;
        const cy = node.y + node.h / 2;

        // 计算原始锚点在全局坐标系的位置
        const anchorVecLocal = getLocalHandleCoords(anchorId, node.w, node.h) || {
          x: 0,
          y: 0,
        };
        const anchorXGlobal = cx + (anchorVecLocal.x * cos - anchorVecLocal.y * sin);
        const anchorYGlobal = cy + (anchorVecLocal.x * sin + anchorVecLocal.y * cos);

        // 计算新尺寸下锚点在局部坐标系的向量
        const newAnchorVecLocal = getLocalHandleCoords(anchorId, newW, newH) || {
          x: 0,
          y: 0,
        };

        // 将新锚点向量转换到全局坐标系
        const newAnchorVecGlobalX = newAnchorVecLocal.x * cos - newAnchorVecLocal.y * sin;
        const newAnchorVecGlobalY = newAnchorVecLocal.x * sin + newAnchorVecLocal.y * cos;

        // 计算新的中心点，使锚点保持在原位置
        const newCx = anchorXGlobal - newAnchorVecGlobalX;
        const newCy = anchorYGlobal - newAnchorVecGlobalY;

        // 根据新中心点计算新的左上角坐标
        patch = {
          x: newCx - newW / 2,
          y: newCy - newH / 2,
          w: newW,
          h: newH,
        };
      }

      const isPointHandler = pointerHandlerTypes.includes(handleId);

      if (node.type === TYPES.text) {
        if (isPointHandler) {
          const originalFontSize = node.fontSize || 16;
          const scaleX = (patch.w ?? node.w) / node.w;
          const newFontSize = Math.floor(originalFontSize * scaleX);
          patch.fontSize = newFontSize;
        }

        if (
          ['top', 'bottom', 'left', 'right'].includes(handleId) &&
          node.width === LayoutSizeType.hug
        ) {
          patch.width = 'fixed';
        }
      }

      if (node.type === TYPES.path) {
        const childBox = provider.getMovingNodeInitialBox(node.id);
        patch.path = scalePathByDimensions(
          childBox.path,
          childBox.w,
          childBox.h,
          patch.w,
          patch.h,
          false,
        );
      }

      provider.stageContext.api.updateNode(node.id, patch);

      if (node.type === TYPES.group) {
        const children = hitTestContext.stageContext.api.getChildren(node.id);
        const scaleX = (patch.w ?? node.w) / node.w;
        const scaleY = (patch.h ?? node.h) / node.h;
        updateChildrenRecursively(provider, children, {
          scaleX,
          scaleY,
          isPointHandler,
        });
      }

      updateGroupSize(provider.stageContext, [
        {
          node: node,
        },
      ]);
      provider.hoverHandlerId = provider.movingHandler.id;
      provider.onChange && provider.onChange();
      hitTestContext.stageContext.hooks.onNodeResizeCallback();
      return true;
    }
  } else if (hitTestContext.type === EventTypes.up) {
    provider.movingHandler = null;
    // reset align cache at end of resize gesture
    provider.alignCache = null;
  }
}

export const updateChildrenRecursively = (provider, children, options, delta = {}) => {
  const { scaleX, scaleY, isPointHandler = true } = options;
  const { deltaX = 0, deltaY = 0 } = delta;
  children.forEach((child) => {
    const childBox = provider.getMovingNodeInitialBox(child.id);
    const newX = Math.round(childBox.x * scaleX) + deltaX;
    const newY = Math.round(childBox.y * scaleY) + deltaY;
    const newW = Math.round(childBox.w * scaleX);
    const newH = Math.round(childBox.h * scaleY);
    const updateData = {
      x: newX,
      y: newY,
      w: newW,
      h: newH,
    };
    // If child is text type, scale font size proportionally
    if (child.type === TYPES.text && isPointHandler) {
      const originalFontSize = childBox.fontSize || 16;
      const padding = getPaddingBox(child);
      const scaleFactor = (newW - padding.x) / (childBox.w - padding.x);
      const { action } = provider.stageContext.providers;
      const isMetaKeyPressed = action.isMetaKeyPressed;
      if (!isMetaKeyPressed) {
        updateData.fontSize = Math.floor(originalFontSize * scaleFactor);
      }
    }
    if (child.type === TYPES.path) {
      const patch = scalePathNode(childBox, updateData.w, updateData.h, false);
      Object.assign(updateData, patch);
    }
    if (child.type === TYPES.line) {
      const patch = {
        size: Math.round(childBox.size * Math.min(scaleX, scaleY)),
      };
      Object.assign(updateData, patch);
    }
    if (child.type === TYPES.shape && childBox.strokeWidth) {
      const patch = {
        strokeWidth: Math.round(childBox.strokeWidth * Math.min(scaleX, scaleY)),
      };
      Object.assign(updateData, patch);
    }
    provider.stageContext.api.updateNode(child.id, updateData);

    // If child is a group, recursively update its children
    if (child.type === TYPES.group) {
      const grandChildren = provider.stageContext.api.getChildren(child.id);
      updateChildrenRecursively(provider, grandChildren, options);
    }
  });
};

// Line节点控制点拖拽处理
function handleLinePointResize(hitTestContext) {
  const provider = hitTestContext.provider;

  if (hitTestContext.type === EventTypes.down) {
    const node = hitTestContext.targetHandler.node;
    const pointIndex = hitTestContext.targetHandler.pointIndex;

    // 缓存初始状态，包括初始点位置
    const points = deserializePoints(node.points);
    provider.movingHandler = {
      id: hitTestContext.targetHandler.id,
      type: hitTestContext.targetHandler.type,
      node: node,
      pointIndex: pointIndex,
      cache: {
        ...node,
        points: [...points], // 缓存初始点数组
      },
      point: {
        ...hitTestContext.point,
      },
    };
    provider.hoverHandlerId = hitTestContext.targetHandler.id;
    return true;
  } else if (hitTestContext.type === EventTypes.move) {
    if (provider.movingHandler && provider.movingHandler.pointIndex !== undefined) {
      const pointIndex = provider.movingHandler.pointIndex;
      const cacheNode = provider.movingHandler.cache;
      const stageContext = hitTestContext.stageContext;
      const isShiftKeyPressed = stageContext.providers.action.isShiftKeyPressed;

      // 以节点局部坐标系更新：将舞台坐标差旋转回节点角度（以匹配区域空间）
      const region = stageContext.api.getNodeRegion(cacheNode);
      const angle = (region.angle * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const rawMoveX = hitTestContext.point.x - provider.movingHandler.point.x;
      const rawMoveY = hitTestContext.point.y - provider.movingHandler.point.y;
      let moveX = rawMoveX * cos + rawMoveY * sin;
      let moveY = -rawMoveX * sin + rawMoveY * cos;

      // 如果按住Shift键，在局部坐标系中限制为垂直或水平移动
      if (isShiftKeyPressed) {
        const absMoveX = Math.abs(moveX);
        const absMoveY = Math.abs(moveY);

        if (absMoveX > absMoveY) {
          // 水平移动为主，锁定Y轴
          moveY = 0;
        } else {
          // 垂直移动为主，锁定X轴
          moveX = 0;
        }
      }

      // 转换为相对于节点尺寸的坐标变化
      // 参考renderLine中的转换：point.x * node.w, point.y * node.h
      const relativeMoveX = moveX / cacheNode.w;
      const relativeMoveY = moveY / cacheNode.h;

      // 获取缓存的初始点数组
      const initialPoints = provider.movingHandler.cache.points;
      if (initialPoints && initialPoints.length > pointIndex) {
        // 基于初始位置计算新位置
        const newPoints = [...initialPoints];
        const originalPoint = initialPoints[pointIndex];
        const newX = originalPoint.x + relativeMoveX;
        const newY = originalPoint.y + relativeMoveY;

        newPoints[pointIndex] = {
          x: newX,
          y: newY,
        };

        // 检查是否需要扩展节点边界
        let minX = Math.min(...newPoints.map((p) => p.x));
        let minY = Math.min(...newPoints.map((p) => p.y));
        let maxX = Math.max(...newPoints.map((p) => p.x));
        let maxY = Math.max(...newPoints.map((p) => p.y));

        // 确保边界至少包含 0-1 范围
        minX = Math.min(0, minX);
        minY = Math.min(0, minY);
        maxX = Math.max(1, maxX);
        maxY = Math.max(1, maxY);

        // 如果范围超出了 0-1，需要重新计算节点边界和点坐标
        if (minX < 0 || minY < 0 || maxX > 1 || maxY > 1) {
          // 计算新的节点尺寸和位置
          const scaleX = maxX - minX;
          const scaleY = maxY - minY;
          const newWidth = scaleX * cacheNode.w;
          const newHeight = scaleY * cacheNode.h;

          // 在局部坐标系中计算位置偏移
          const deltaX = minX * cacheNode.w;
          const deltaY = minY * cacheNode.h;

          // 将局部坐标系的偏移转换到全局坐标系
          const globalDeltaX = deltaX * cos - deltaY * sin;
          const globalDeltaY = deltaX * sin + deltaY * cos;

          const newNodeX = cacheNode.x + globalDeltaX;
          const newNodeY = cacheNode.y + globalDeltaY;

          // 重新计算所有点的相对坐标
          const adjustedPoints = newPoints.map((point) => ({
            x: (point.x - minX) / scaleX,
            y: (point.y - minY) / scaleY,
          }));

          // 更新节点位置、尺寸和点坐标
          stageContext.api.updateNode(cacheNode.id, {
            x: newNodeX,
            y: newNodeY,
            w: newWidth,
            h: newHeight,
            points: serializePoints(adjustedPoints),
          });
        } else {
          // 点在 0-1 范围内，只需限制边界并更新点坐标
          newPoints[pointIndex] = {
            x: Math.max(0, Math.min(1, newX)),
            y: Math.max(0, Math.min(1, newY)),
          };

          // 只更新点坐标
          stageContext.api.updateNode(cacheNode.id, {
            points: serializePoints(newPoints),
          });
        }

        provider.onChange && provider.onChange();
        stageContext.hooks.onNodeResizeCallback();
      }
      return true;
    }
  } else if (hitTestContext.type === EventTypes.up) {
    // 移除拖拽提示
    const tip = document.getElementById('line-drag-tip');
    if (tip) tip.remove();

    provider.movingHandler = null;
  }

  return false;
}
